"use client";

import { useEffect, useState } from "react";
import { Quote } from "lucide-react";
import { Carousel, CarouselApi, CarouselContent, CarouselItem } from "@/components/ui/carousel";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Perspectives() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const [api, setApi] = useState<CarouselApi>();
  const [current, setCurrent] = useState(0);
  const primaryTestimonials = [{
    quote: "Myles saw the narrative potential in our brand that we had been overlooking for years. His strategic clarity is unmatched.",
    name: "Elena Vance",
    position: "Founder, Lumina"
  }, {
    quote: "Working with Myles is a masterclass in restraint. He strips away the noise until only the most powerful message remains.",
    name: "Marcus Thorne",
    position: "CEO, Thorne Creative"
  }, {
    quote: "He doesn't just consult; he architects identity. Our market presence has never felt more authentic or more authoritative.",
    name: "Sarah Jenkins",
    position: "Director, Velo"
  }, {
    quote: "The 5ONDER framework changed how we approach every single touchpoint. It is precise, actionable, and deeply effective.",
    name: "David Chen",
    position: "CMO, Zenith"
  }, {
    quote: "Myles brings a level of editorial rigor to marketing that is rare. He treats every campaign like a piece of art.",
    name: "Julian Ross",
    position: "Founder, Studio 8"
  }];
  const secondaryTestimonials = [{
    quote: "A rare consultant who understands that silence is as important as the message itself. Truly transformative.",
    name: "Nina Gupta",
    position: "Partner, Aura"
  }, {
    quote: "His ability to distill complex brand challenges into simple, elegant solutions is his greatest strength.",
    name: "Simon H.",
    position: "Creative Lead"
  }];
  useEffect(() => {
    if (!api) return;
    setCurrent(api.selectedScrollSnap());
    const onSelect = () => setCurrent(api.selectedScrollSnap());
    api.on("select", onSelect);
    return () => {
      api.off("select", onSelect);
    };
  }, [api]);
  return <section data-section-id="3075" id="client-perspectives" className="relative overflow-hidden bg-background text-foreground py-24 md:py-40 px-6 md:px-12">
      <div ref={ref} className={`relative mx-auto max-w-6xl transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
        {}
        <div className="text-center mb-8">
          <span className="font-default text-[0.75rem] tracking-[0.25em] uppercase" style={{
          color: "var(--primary)"
        }}>
            Client Perspectives
          </span>
        </div>

        {}
        <h2 className="font-default text-center text-5xl md:text-7xl font-medium tracking-[0.15em] leading-[0.9] text-foreground mb-20 md:mb-28">
          <span className="italic">In their</span>
          <br />
          <span>own words</span>
        </h2>

        {}
        <div className="relative">
          {}
          <div aria-hidden="true" className="pointer-events-none absolute inset-0 flex items-center justify-center">
            <div className="relative w-[420px] h-[420px] max-w-full opacity-100">
              <div className="absolute inset-0 border" style={{
              borderColor: "var(--border)"
            }} />
              <div className="absolute inset-8 border" style={{
              borderColor: "var(--border)"
            }} />
              <div className="absolute inset-16 border" style={{
              borderColor: "var(--border)"
            }} />
              <div className="absolute inset-28 border" style={{
              borderColor: "var(--border)"
            }} />
              <div className="absolute inset-40 border" style={{
              borderColor: "var(--border)"
            }} />
            </div>
          </div>

          <Carousel setApi={setApi} opts={{
          loop: true,
          align: "start"
        }} className="relative">
            <CarouselContent>
              {primaryTestimonials.map((t, i) => <CarouselItem key={i} data-index={i}>
                  <div className="relative max-w-3xl mx-auto px-4 md:px-12 py-8 md:py-12">
                    {}
                    <Quote aria-hidden="true" className="absolute -top-2 -left-2 md:-top-6 md:-left-6 w-20 h-20 md:w-24 md:h-24" style={{
                  color: "var(--primary)"
                }} strokeWidth={1} fill="currentColor" />

                    <blockquote className="relative">
                      <p className="font-default italic text-2xl md:text-[2.5rem] leading-[1.25] text-center text-foreground">
                        {t.quote}
                      </p>
                    </blockquote>

                    {}
                    <div className="flex justify-center my-8 md:my-10">
                      <div className="h-px w-24" style={{
                    backgroundColor: "var(--primary)"
                  }} />
                    </div>

                    {}
                    <div className="text-center font-default text-[0.75rem] tracking-[0.25em] uppercase">
                      <span className="text-foreground">{t.name}</span>
                      <span className="mx-2" style={{
                    color: "var(--ink-muted)"
                  }}>
                        /
                      </span>
                      <span style={{
                    color: "var(--ink-muted)"
                  }}>
                        {t.position}
                      </span>
                    </div>
                  </div>
                </CarouselItem>)}
            </CarouselContent>
          </Carousel>

          {}
          <div className="flex items-center justify-center gap-4 mt-8">
            {primaryTestimonials.map((_, i) => <button key={i} data-index={i} aria-label={`Go to testimonial ${i + 1}`} onClick={() => api?.scrollTo(i)} className="group py-3">
                <span className="block h-px transition-all duration-500" style={{
              width: current === i ? "3rem" : "2rem",
              backgroundColor: current === i ? "var(--primary)" : "var(--border)",
              opacity: current === i ? 1 : 0.6
            }} />
              </button>)}
          </div>
        </div>

        {}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-20 mt-24 md:mt-32 max-w-5xl mx-auto">
          {secondaryTestimonials.map((t, i) => <div key={i} data-index={i} className="relative">
              <Quote aria-hidden="true" className="w-8 h-8 mb-4" style={{
            color: "var(--primary)"
          }} strokeWidth={1} fill="currentColor" />
              <blockquote>
                <p className="font-default italic text-xl md:text-2xl leading-snug text-foreground">
                  {t.quote}
                </p>
              </blockquote>

              <div className="my-6">
                <div className="h-px w-16" style={{
              backgroundColor: "var(--primary)"
            }} />
              </div>

              <div className="font-default text-[0.75rem] tracking-[0.25em] uppercase">
                <span className="text-foreground">{t.name}</span>
                <span className="mx-2" style={{
              color: "var(--ink-muted)"
            }}>
                  /
                </span>
                <span style={{
              color: "var(--ink-muted)"
            }}>{t.position}</span>
              </div>
            </div>)}
        </div>
      </div>
    </section>;
}